// Cliente de /api/oficina/pelotas (backend/src/api/pelotas.routes.ts; contrato en docs/pelotas/ux-2026-11-03/
// construccion.md): los contratos con Santi, cada conversación, el chat con Santi, el agente de cada hilo y su propuesta
// (entrega 3) y los actos de Pau (escribir, pedir algo, Hecho/Cancelar, marcar visto). Lo que sale hacia Santi lleva
// `Idempotency-Key`: un reintento con la misma clave no lo repite.
import { api } from '../../../lib/api';
import type { AgenteSantiDTO, HiloDTO, HiloGeneralDTO, ListaContratosDTO, MotivoCierre, PropuestaDTO } from './tipos';

const BASE = '/api/oficina/pelotas';

/**
 * Lo que acepta el servidor de la oficina (backend/src/api/pelotas.routes.ts: `Pedir`, `Mensaje`, `Cerrar`; el centro
 * admite lo mismo o más: título 3-120, detalle 20 000). Las cajas no dejan escribir más (maxLength) y avisan cerca del
 * límite; si aun así el servidor dice que no, el aviso dice qué sobra. Si cambian allí, cambian aquí.
 */
export const LIMITES = { titulo: 120, texto: 4000, nota: 2000 } as const;

export const pelotasApi = {
  lista: () => api<ListaContratosDTO>(BASE),
  hilo: (temaId: number) => api<HiloDTO>(`${BASE}/temas/${temaId}`),
  general: () => api<HiloGeneralDTO>(`${BASE}/temas/general`),
  /**
   * Un mensaje de Pau a Santi. En un contrato que le toca a Pau, enviar le pasa el turno a Santi (la oficina deriva lo
   * que le toca); `seguirYo` («No, sigo yo») manda solo el mensaje.
   */
  mensaje: (hilo: number | 'general', cuerpo: { texto: string; seguirYo?: boolean }, clave: string) =>
    api<{ seq: number; en: string; lado: string | null }>(`${BASE}/temas/${hilo}/mensajes`, {
      method: 'POST',
      body: cuerpo.seguirYo ? { texto: cuerpo.texto, seguirYo: true } : { texto: cuerpo.texto },
      headers: { 'Idempotency-Key': clave },
    }),
  /** «Hecho» o «Cancelar» (cualquier contrato abierto; nota vacía → «Hecho.» / «Cancelado.»). Sale al irse la línea de 8 s. */
  cerrar: (temaId: number, cuerpo: { nota?: string; motivo: MotivoCierre }, clave: string) =>
    api<{ seq: number; en: string }>(`${BASE}/temas/${temaId}/cerrar`, {
      method: 'POST',
      body: cuerpo.nota ? { nota: cuerpo.nota, motivo: cuerpo.motivo } : { motivo: cuerpo.motivo },
      headers: { 'Idempotency-Key': clave },
    }),
  /**
   * «Pedir algo a Santi»: abre un contrato pedido por Pau con el turno en Santi (título y «qué falta» los deriva la oficina
   * si faltan). `borrador`: el agente cuyo pedido preparado era lo que sale (el servidor lo usa y lo borra).
   */
  pedir: (cuerpo: { titulo?: string; texto: string; fechaLimite?: string; borrador?: string }, clave: string) =>
    api<{ temaId: number }>(`${BASE}/temas`, { method: 'POST', body: cuerpo, headers: { 'Idempotency-Key': clave } }),
  /** Pau ha visto hasta `hasta` (el último seq) en ese contrato o en el chat: se apaga el punto blanco. Solo su sesión. */
  visto: (hilo: number | 'general', hasta: number) => api<unknown>(`${BASE}/temas/${hilo}/visto`, { method: 'POST', body: { hasta } }),
  /**
   * El agente de un hilo: si ya tiene uno vivo, el suyo; si no, la oficina le pone uno (con el expediente del contrato).
   * Entrega 3: la ventana lo pide al abrir un hilo sin agente («Poniendo un agente…»). `general`: el del chat con Santi.
   * Ponerlo es solo de la sesión de Pau (403 SOLO_PAU a otra); `agentId: null`: un contrato cerrado sin agente.
   */
  agente: (hilo: number | 'general') => api<{ agentId: string | null }>(`${BASE}/temas/${hilo}/agente`, { method: 'POST', body: {} }),
  /** «Que lo prepare tu agente»: el agente de ese hilo prepara una propuesta, que sale en SU chat (202; llega por el socket). */
  preparar: (hilo: number | 'general') => api<unknown>(`${BASE}/temas/${hilo}/preparar`, { method: 'POST', body: {} }),
  /**
   * Entrega 3: «Usar en la caja» — la propuesta vigente queda usada y vuelve entera (su texto va a la caja, editable).
   * `mensajeId`: la tarjeta que se pulsó; si ya no es la vigente, 409 PROPUESTA_SUSTITUIDA. Sin propuesta, 404.
   */
  usarPropuesta: (hilo: number | 'general', mensajeId?: string | null) =>
    api<PropuestaDTO>(`${BASE}/temas/${hilo}/propuesta/usar`, { method: 'POST', body: mensajeId ? { mensajeId } : {} }),
  /** Lo que la web pinta en el chat de un agente: su «Pedir algo» preparado («… · Revisar»), o null. */
  agenteSanti: (agentId: string) => api<AgenteSantiDTO>(`${BASE}/agentes/${encodeURIComponent(agentId)}`),
  /** «Descartar» el «Pedir algo» que preparó ese agente. */
  descartarPedido: (agentId: string) => api<unknown>(`${BASE}/agentes/${encodeURIComponent(agentId)}/pedido/descartar`, { method: 'POST', body: {} }),
};
